import { useCallback, useContext, useEffect } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  SmartSendItemsAddedSocketAction,
  SmartSendItemsUpdatedSocketAction
} from '../../../lib/context/SmartSendContextModels';
import { Codes, SocketMarketplaceWorkerNotificationModel } from '../SmartSendSocketsModels';
import { chunk } from '../../../lib/helpers/ArrayHelper';
import { SmartSendItemsCache } from '../../../lib/cache/SmartSendItemsCache';
import { IsSmartSend } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ConcernsContext } from '../../../lib/context/ConcernsContext';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { GetAccountId, GetSocketSessionId } from '../../../lib/helpers/AuthHelper';
import { useAuth } from 'react-oidc-context';
import { useSocket } from '@parcel2go/npm-p2g-app';
import { uid } from '../../../lib/helpers/uid';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { Consts, NotificationsEventType } from '../../../lib/em/EmitTypes';
import { polyfillAccountIdPrefix } from '../../../lib/helpers/MarketplaceAccountIdHelper';
import useOnLoad from '~/routes/use-on-load';

const useSocketsBackgroundTask = () => {
  const auth = useAuth();
  const { getItemsByIds } = useSmartSendApi();
  const { dispatch: sctxDispatch } = useContext(SmartSendContext);
  const { dispatch: cctxDispatch } = useContext(ConcernsContext);
  const { emit } = useEventEmitter<NotificationsEventType>();

  const { lastJsonMessage, sendJsonMessage } = useSocket({
    sessionId: GetSocketSessionId(auth?.user?.profile),
    ready: auth.isAuthenticated
  });

  useOnLoad(() => {
    sendJsonMessage?.({
      type: 'smartsend_started',
      payload: {
        host: window.location.hostname,
        account: GetAccountId(auth?.user?.profile),
        profile: SettingsCache.profile
      }
    });
  });

  const chunkGetItems = useCallback(
    async (type: 'SOLD', ids: { Id: string }[], size) => {
      const existingIds = SmartSendItemsCache.filter(IsSmartSend).map((x) => x.Id);

      const chunks = chunk(
        ids.filter((x) => !existingIds.includes(x.Id)).map((x) => x.Id),
        size
      );

      for (const c of chunks) {
        const res = await getItemsByIds(c);
        if (res.success) {
          switch (type) {
            case 'SOLD':
              sctxDispatch(new SmartSendItemsAddedSocketAction(res.result.Items, chunks.indexOf(c)));
              break;
          }
        }
      }
    },
    [getItemsByIds, sctxDispatch]
  );

  const onLastJsonMessage = useCallback(
    async (lastJsonMessage: unknown) => {
      const { action, data } = lastJsonMessage as { action: string; data: any };
      switch (action) {
        case 'smartsend_new_items':
          {
            console.log('BGTASK: SOCKET:SMARTSEND_NEW_ITEMS', data);
            await chunkGetItems('SOLD', data.Items, 100);
            emit(Consts.Notifications, { message: `${data.Items.length} New Items`, variant: 'success', key: uid() });
          }
          break;

        case 'smartsend_items_updated':
          {
            console.log('BGTASK: SOCKET:SMARTSEND_ITEMS_UPDATED', data);
            sctxDispatch(new SmartSendItemsUpdatedSocketAction(data));

            // If this is a dispatch fail we map to marketplace worker style notifications.
            // and handle them in the concerns context.
            if (
              data.Items &&
              data.Items.length > 0 &&
              data.Items.some((x) => x?.ItemUpdates?.DispatchStatus === 'Failed')
            ) {
              const failedItemSockets = data.Items.filter((x) => x?.ItemUpdates?.DispatchStatus === 'Failed');

              failedItemSockets.forEach((itemUpdateModel) => {
                const cachedItem = SmartSendItemsCache.find((i) => i.Id === itemUpdateModel.Id);
                if (!cachedItem) {
                  console.warn(`Item with ID ${itemUpdateModel.Id} not found in cache.`);
                  return;
                }

                // Worker jobs raising these are for one marketplace account so the source is consistent.
                const source = cachedItem.MarketplaceOrderLines?.[0]?.Source;
                // Create a new model for the accounts concerns notification
                const newModel: SocketMarketplaceWorkerNotificationModel = {
                  Details: {
                    // TODO - fix this in the items API. The items API currently gives these as just the int on the calls that fill the items cache.
                    // But in other places we use the full account ID with the prefix. This should be consistent.
                    MPAccountId: polyfillAccountIdPrefix(source.Id, source.AccountId),
                    MarketplaceSource: source.Id,
                    Stage: 'update',
                    SentTime: new Date(),
                    UserId: '',
                    CustomerId: GetAccountId(auth.user?.profile),
                    Affected: 1,
                    Status: 'Failed',
                    Code: Codes.AutoDispatchFail,
                    Info: `Failed to update tracking for one or more orders. Retry manually in Notify Marketplace`
                  }
                };

                cctxDispatch({
                  type: 'ADD-SOCKET',
                  payload: newModel
                });
              });
            }
          }
          break;

        case 'marketplace_worker_notification':
          {
            console.log('BGTASK: SOCKET:MARKETPLACE_WORKER_NOTIFICATION', data);
            if (data.Details) {
              data.Details.SentTime = new Date(data.Details.SentTime);
            }
            cctxDispatch({ type: 'ADD-SOCKET', payload: data });
          }
          break;
      }
    },
    [chunkGetItems, sctxDispatch, emit, cctxDispatch, auth.user]
  );

  // Manage incoming socket messages
  useEffect(() => {
    if (lastJsonMessage) {
      onLastJsonMessage(lastJsonMessage);
    }
  }, [lastJsonMessage, onLastJsonMessage]);
};

export default useSocketsBackgroundTask;
